import { Spacing } from '@persona-id/design-tokens';
import { startCase, uniq } from 'lodash/fp';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import {
  MATCH_REQUIREMENT_TO_DESCRIPTION,
  MATCH_REQUIREMENT_TO_DISPLAY_NAME,
} from 'dashboard/components/ExtractionPanel/constants';
import {
  guidedExtractionRequestToPresetGenericFormState,
  guidedPresetGenericFormStateToExtractionRequest,
  validateGuidedExtractionGenericPanelForm,
} from 'dashboard/components/ExtractionPanel/guided_generic_extraction';
import GuidedAddressExtractionPanel, {
  guidedExtractionRequestToPresetAddressFormState,
  guidedPresetAddressFormStateToExtractionRequest,
  validateGuidedExtractionAddressPanelForm,
} from 'dashboard/components/ExtractionPanel/GuidedAddressExtractionPanel';
import GuidedNameExtractionPanel, {
  guidedExtractionRequestToPresetNameFormState,
  guidedPresetNameFormStateToExtractionRequest,
  validateGuidedExtractionNamePanelForm,
} from 'dashboard/components/ExtractionPanel/GuidedNameExtractionPanel';
import {
  ExtractionCategory,
  ExtractionPanelForm,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import {
  ExtractionRequest,
  GuidedExtractionRequestV3,
  GuidedPreset,
} from 'dashboard/models/document';
import { ErrorText } from 'design-system/components/FormControl';
import { MenuHeading } from 'design-system/components/Menu';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { assertNever } from 'lib/utils/types';

const EXTRACTION_CATEGORY_TO_DESCRIPTION: Record<ExtractionCategory, string> = {
  [ExtractionCategory.Address]: 'Map inquiry fields to an address schema.',
  [ExtractionCategory.Name]: 'Map inquiry fields to a full name schema.',
};

const EXTRACTION_CATEGORY_MATCH_REQUIREMENT_TO_DESCRIPTION: Record<
  ExtractionCategory,
  Record<MatchRequirement, string | undefined>
> = {
  [ExtractionCategory.Address]: {
    [MatchRequirement.Strict]:
      'Leverages AI to accommodate variations in matches for street 1 and either city and subdivision or city and postal code.',
    [MatchRequirement.Loose]:
      'Leverages AI to accommodate variations in matches for street 1 and at least one of city, subdivision, or postal code.',
    [MatchRequirement.LooseAi]: undefined,
  },
  [ExtractionCategory.Name]: {
    [MatchRequirement.Strict]:
      'Requires strict matches for both first name and last name; tolerates minor OCR errors to account for common discrepancies.',
    [MatchRequirement.Loose]:
      'Leverages AI to accommodate variations in first and last names; suitable for common name discrepancies.',
    [MatchRequirement.LooseAi]: undefined,
  },
};

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const Section = styled.section`
  padding: 0 ${Spacing[4]} ${Spacing[4]} ${Spacing[4]};
`;

const SectionBreak = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface GuidedExtractionPanelProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  possibleInquiryFieldNames: string[];
  disabled?: boolean;
}

export const guidedExtractionV3RequestToFormState = (
  extractionRequest: GuidedExtractionRequestV3,
): Partial<ExtractionPanelForm> => {
  switch (extractionRequest.preset) {
    case GuidedPreset.AddressLoose:
    case GuidedPreset.AddressStrict:
      return guidedExtractionRequestToPresetAddressFormState(extractionRequest);
    case GuidedPreset.NameLoose:
    case GuidedPreset.NameStrict:
      return guidedExtractionRequestToPresetNameFormState(extractionRequest);
    case GuidedPreset.GenericLoose:
    case GuidedPreset.GenericLooseAi:
    case GuidedPreset.GenericStrict:
      return guidedExtractionRequestToPresetGenericFormState(extractionRequest);
    default:
      assertNever(extractionRequest.preset as never);
  }
};

export const guidedFormStateToExtractionV3Request = (
  data: ExtractionPanelForm,
): ExtractionRequest => {
  switch (data.extractionCategory) {
    case ExtractionCategory.Address:
      return guidedPresetAddressFormStateToExtractionRequest(data);
    case ExtractionCategory.Name:
      return guidedPresetNameFormStateToExtractionRequest(data);
    default:
      return guidedPresetGenericFormStateToExtractionRequest(data);
  }
};

const fieldNameRegex = /^[_\s]*[a-zA-Z0-9][_a-zA-Z0-9\s]*$/;

export const validateGuidedExtractionV3PanelForm = (
  data: ExtractionPanelForm,
  unconfigurableInquiryFieldNames: string[],
) => {
  const errorRequired = { type: 'required', message: 'Required' };
  let errors: FieldErrors<ExtractionPanelForm> = {};

  if (data?.fieldName == null || data?.fieldName === '') {
    errors.fieldName = errorRequired;
  } else if (!data?.fieldName.match(fieldNameRegex)) {
    errors.fieldName = {
      type: 'pattern',
      message: 'Extraction label may only contain letters, number, underscores, and spaces.',
    };
  }

  if (data?.extractionCategory == null) {
    errors.extractionCategory = errorRequired;
  }
  if (data?.matchRequirement == null) {
    errors.matchRequirement = errorRequired;
  }
  switch (data?.extractionCategory) {
    case ExtractionCategory.Address:
      errors = {
        ...errors,
        ...validateGuidedExtractionAddressPanelForm(data, unconfigurableInquiryFieldNames),
      };
      break;
    case ExtractionCategory.Name:
      errors = {
        ...errors,
        ...validateGuidedExtractionNamePanelForm(data, unconfigurableInquiryFieldNames),
      };
      break;
    default:
      errors = {
        ...errors,
        ...validateGuidedExtractionGenericPanelForm(data, unconfigurableInquiryFieldNames),
      };
      break;
  }
  return errors;
};

const GuidedExtractionV3Panel = ({
  formReturn,
  possibleInquiryFieldNames,
  disabled = false,
}: GuidedExtractionPanelProps) => {
  const { formState, watch, setValue, control } = formReturn;
  const { errors } = formState;
  const extractionCategory = watch('extractionCategory');
  const matchRequirement = watch('matchRequirement');

  let extractionPanel = null;
  switch (extractionCategory) {
    case ExtractionCategory.Address:
      extractionPanel = (
        <GuidedAddressExtractionPanel
          formReturn={formReturn}
          possibleInquiryFieldNames={possibleInquiryFieldNames}
          disabled={disabled}
        />
      );
      break;
    case ExtractionCategory.Name:
      extractionPanel = (
        <GuidedNameExtractionPanel
          formReturn={formReturn}
          possibleInquiryFieldNames={possibleInquiryFieldNames}
          disabled={disabled}
        />
      );
      break;
    default:
      extractionPanel = null;
      break;
  }

  return (
    <>
      <Section>
        <StyledLabel>Select inquiry field for comparison</StyledLabel>
        <Controller
          name='extractionCategory'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select
                {...field}
                value={field.value}
                placeholder='Select extraction type'
                hasError={!!errors?.extractionCategory}
                onValueChange={(e) => {
                  field.onChange(e);

                  if (matchRequirement == null) {
                    switch (e) {
                      case ExtractionCategory.Address:
                      case ExtractionCategory.Name:
                        setValue('matchRequirement', MatchRequirement.Loose);
                        break;
                      default:
                        setValue('matchRequirement', MatchRequirement.Strict);
                        break;
                    }
                  } else if (
                    Object.values<string>(ExtractionCategory).includes(e) &&
                    matchRequirement === MatchRequirement.LooseAi
                  ) {
                    setValue('matchRequirement', MatchRequirement.Loose);
                  }
                }}
                disabled={disabled}
              >
                <MenuHeading>Composite Inquiry Fields </MenuHeading>
                {Object.values(ExtractionCategory).map((category) => (
                  <Option
                    key={category}
                    value={category}
                    subtitle={EXTRACTION_CATEGORY_TO_DESCRIPTION[category]}
                  >
                    {startCase(category)}
                  </Option>
                ))}
                <MenuHeading>Single Inquiry Fields </MenuHeading>
                {uniq([
                  ...possibleInquiryFieldNames,
                  ...(Object.values<string>(ExtractionCategory).includes(field.value)
                    ? []
                    : [field.value]),
                ])
                  .filter((value) => value != null)
                  .map((field) => (
                    <Option key={field} value={field}>
                      {startCase(field)}
                    </Option>
                  ))}
              </Select>
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.extractionCategory?.message} />
        <Spacer height={1.5} />
        <StyledLabel>Match requirement</StyledLabel>
        <Controller
          name='matchRequirement'
          control={control}
          render={({ field }) => {
            const descriptions =
              EXTRACTION_CATEGORY_MATCH_REQUIREMENT_TO_DESCRIPTION[
                extractionCategory as ExtractionCategory
              ] ?? MATCH_REQUIREMENT_TO_DESCRIPTION;

            return (
              <FieldWrapper>
                <Select
                  {...field}
                  placeholder='Select match requirement'
                  hasError={!!errors?.matchRequirement}
                  disabled={disabled || !extractionCategory}
                >
                  {Object.values(MatchRequirement).map(
                    (matchRequirement) =>
                      descriptions[matchRequirement] && (
                        <Option
                          key={matchRequirement}
                          value={matchRequirement}
                          subtitle={descriptions[matchRequirement]}
                        >
                          {MATCH_REQUIREMENT_TO_DISPLAY_NAME[matchRequirement]}
                        </Option>
                      ),
                  )}
                </Select>
              </FieldWrapper>
            );
          }}
        />
        <FieldErrorText message={errors?.matchRequirement?.message} />
      </Section>
      <SectionBreak />
      {extractionPanel}
    </>
  );
};

export default GuidedExtractionV3Panel;
